/**
 * Referrals.
 *
 * Two halves that happen at different times in an account's life: entering
 * somebody else's code, which is only possible before the first pack or
 * deposit, and handing out your own, which is possible forever. The redeem
 * panel disappears once the window closes rather than sitting there greyed out,
 * because an input you can never use again is worse than no input.
 *
 * The page is explicit that bonus credit is not withdrawable. That is a real
 * constraint on money someone has been given, and burying it until they try to
 * withdraw would be the dishonest way to ship this.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { EmptyState, SectionHeading, Skeleton, Stat } from '../components/ui'
import { useGenerateReferralCode, useRedeemReferralCode, useReferral } from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import { ApiError, type ReferralSummary } from '../lib/api'
import { cents, dateOnly } from '../lib/format'

export function ReferralsPage() {
  const { user, connectAndSignIn } = useAuth()
  const { data, isLoading } = useReferral(Boolean(user))

  if (!user) {
    return (
      <div className="py-10">
        <EmptyState
          title="Sign in to refer a friend"
          body="Referral codes belong to an account, so this needs a signed-in wallet."
          action={
            <button className="btn btn-primary btn-sm" onClick={() => void connectAndSignIn()}>
              Connect wallet
            </button>
          }
        />
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <div className="py-6">
      <SectionHeading
        title="Refer a friend"
        sub={`They get ${cents(data.signupBonusCents)} to play with. You get ${data.depositSharePercent.toFixed(0)}% of their first deposit.`}
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="space-y-5">
          <YourCode data={data} />
          {data.canRedeem && <Redeem data={data} />}
          <Referred data={data} />
        </div>

        <Terms data={data} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Your code
 * ------------------------------------------------------------------ */

function YourCode({ data }: { data: ReferralSummary }) {
  const generate = useGenerateReferralCode()
  const [copied, setCopied] = useState<'code' | 'link' | null>(null)

  const link = data.code ? `${window.location.origin}/?ref=${data.code}` : ''

  const copy = async (value: string, which: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(which)
      window.setTimeout(() => setCopied(null), 1600)
    } catch {
      // Clipboard is blocked in some contexts; both values are on screen.
    }
  }

  if (!data.code) {
    return (
      <section className="panel p-5">
        <h2 className="text-base font-semibold">Your referral code</h2>
        <p className="mt-1 max-w-lg text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
          You do not have one yet. Generating it is permanent: the code will not change, so it is
          safe to put somewhere lasting.
        </p>
        <button
          className="btn btn-primary btn-sm mt-4"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
        >
          {generate.isPending ? 'Generating…' : 'Generate my code'}
        </button>
        {generate.error && (
          <p className="mt-2 text-xs" role="alert" style={{ color: 'var(--color-down)' }}>
            Could not generate a code. Try again.
          </p>
        )}
      </section>
    )
  }

  return (
    <section className="panel p-5">
      <h2 className="text-base font-semibold">Your referral code</h2>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <code
          className="tnum px-4 py-3 text-2xl font-bold tracking-[0.22em]"
          style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-line)' }}
        >
          {data.code}
        </code>
        <button className="btn btn-ghost btn-sm" onClick={() => void copy(data.code!, 'code')}>
          {copied === 'code' ? 'Copied' : 'Copy code'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => void copy(link, 'link')}>
          {copied === 'link' ? 'Copied' : 'Copy link'}
        </button>
      </div>

      <p className="mt-3 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
        Your friend enters this on their own referrals page, before they open a pack or deposit.
      </p>
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * Redeeming
 * ------------------------------------------------------------------ */

function Redeem({ data }: { data: ReferralSummary }) {
  const [code, setCode] = useState('')
  const redeem = useRedeemReferralCode()

  if (redeem.isSuccess) {
    return (
      <section className="panel p-5" style={{ borderColor: 'var(--color-brand)' }}>
        <h2 className="text-base font-semibold">
          {cents(redeem.data.bonusCents)} added to your balance
        </h2>
        <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
          Referred by {redeem.data.referrerHandle ?? 'another player'}. It is bonus credit, so it
          buys packs but cannot be withdrawn until it has been played through.
        </p>
        <Link to="/packs" className="btn btn-primary btn-sm mt-4">
          Open a pack
        </Link>
      </section>
    )
  }

  return (
    <section className="panel p-5">
      <h2 className="text-base font-semibold">Have a friend's code?</h2>
      <p className="mt-1 max-w-lg text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        Enter it and {cents(data.signupBonusCents)} lands in your balance. This can only be done
        before your first pack or deposit, so do it now if you are going to.
      </p>

      <form
        className="mt-4 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (code.trim()) redeem.mutate(code)
        }}
      >
        <input
          className="panel-2 tnum min-w-[200px] flex-1 px-3 py-2 text-sm tracking-[0.18em] uppercase outline-none"
          placeholder="ABCD2345"
          value={code}
          maxLength={12}
          spellCheck={false}
          autoComplete="off"
          aria-label="Referral code"
          onChange={(e) => {
            setCode(e.target.value.toUpperCase())
            redeem.reset()
          }}
        />
        <button className="btn btn-primary btn-sm" type="submit" disabled={!code.trim() || redeem.isPending}>
          {redeem.isPending ? 'Checking…' : 'Redeem'}
        </button>
      </form>

      {redeem.error && (
        <p className="mt-2 text-xs" role="alert" style={{ color: 'var(--color-down)' }}>
          {redeem.error instanceof ApiError ? redeem.error.message : 'Could not redeem that code.'}
        </p>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * Who you have referred
 * ------------------------------------------------------------------ */

function Referred({ data }: { data: ReferralSummary }) {
  return (
    <section className="panel overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b px-5 py-4">
        <h2 className="text-base font-semibold">
          People you have referred{data.referrals.length ? ` · ${data.referrals.length}` : ''}
        </h2>
        <Stat label="Earned" value={cents(data.totalEarnedCents)} />
      </div>

      {data.referrals.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm" style={{ color: 'var(--color-faint)' }}>
          Nobody yet. Share your code and they will show up here.
        </div>
      ) : (
        <div className="divide-y">
          {data.referrals.map((r, i) => (
            <div key={`${r.username ?? i}`} className="flex items-center gap-4 px-5 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{r.username ?? 'A player'}</div>
                <div className="text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                  Joined {dateOnly(r.joinedAt)}
                  {!r.deposited && ' · has not deposited yet'}
                </div>
              </div>
              <div
                className="tnum text-sm font-semibold"
                style={{ color: r.earnedCents > 0 ? 'var(--color-up)' : 'var(--color-faint)' }}
              >
                {r.earnedCents > 0 ? cents(r.earnedCents) : '-'}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * The rules, in full
 * ------------------------------------------------------------------ */

function Terms({ data }: { data: ReferralSummary }) {
  return (
    <aside className="panel p-5 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
      <div className="eyebrow">How it works</div>

      <dl className="mt-4 space-y-3">
        <div>
          <dt className="font-semibold" style={{ color: 'var(--color-text)' }}>
            They get {cents(data.signupBonusCents)}
          </dt>
          <dd className="mt-0.5">
            Credited the moment they enter your code, which they must do before opening a pack or
            depositing.
          </dd>
        </div>
        <div>
          <dt className="font-semibold" style={{ color: 'var(--color-text)' }}>
            You get {data.depositSharePercent.toFixed(0)}% of their first deposit
          </dt>
          <dd className="mt-0.5">
            Their first deposit only, up to {cents(data.depositCapCents)}. Paid automatically when
            it is credited.
          </dd>
        </div>
        <div>
          <dt className="font-semibold" style={{ color: 'var(--color-text)' }}>
            Bonus credit has to be played
          </dt>
          <dd className="mt-0.5">
            Both bonuses buy packs immediately, but cannot be withdrawn until{' '}
            {data.wagerMultiple > 1 ? `${data.wagerMultiple}× their value has` : 'they have'} been
            staked. Anything you win from them is ordinary balance and withdraws normally.
          </dd>
        </div>
      </dl>

      {data.lockedCents > 0 && (
        <div className="mt-4 border-t pt-4">
          <div className="eyebrow">Your locked credit</div>
          <div className="tnum mt-1 text-lg font-bold" style={{ color: 'var(--color-text)' }}>
            {cents(data.lockedCents)}
          </div>
          <p className="mt-1">
            Spendable on packs. It stops being locked as you stake, and it is not counted as
            withdrawable until then.
          </p>
        </div>
      )}

      {data.referredBy && (
        <div className="mt-4 border-t pt-4">
          You were referred by{' '}
          <span style={{ color: 'var(--color-text)' }}>
            {data.referredBy.username ?? 'another player'}
          </span>{' '}
          on {dateOnly(data.referredBy.at)}.
        </div>
      )}

      <p className="mt-4 border-t pt-4" style={{ color: 'var(--color-faint)' }}>
        Referring yourself with a second wallet is not a loophole worth trying: bonus credit is not
        withdrawable until it has been staked, and staking it is how the house makes its money.
      </p>
    </aside>
  )
}
